import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../../lib/api-client';

interface CostSummary {
  month: string;
  totalCost: number;
  totalInputTokens: number;
  totalOutputTokens: number;
}

interface QueueStats {
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-ll-surface border border-ll-border rounded-xl p-5">
      <p className="text-xs uppercase font-mono text-ll-muted mb-1">{label}</p>
      <p className="text-2xl font-bold text-ll-text">{value}</p>
      {sub && <p className="text-xs text-ll-muted mt-1">{sub}</p>}
    </div>
  );
}

export function Overview() {
  const [costs, setCosts] = useState<CostSummary | null>(null);
  const [queues, setQueues] = useState<QueueStats[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.allSettled([
      get<CostSummary>('/v1/admin/costs'),
      get<{ queues: QueueStats[] }>('/v1/admin/queues'),
    ]).then(([costResult, queueResult]) => {
      if (costResult.status === 'fulfilled') setCosts(costResult.value);
      if (queueResult.status === 'fulfilled') setQueues(queueResult.value.queues);
      if (costResult.status === 'rejected' && queueResult.status === 'rejected') {
        setError('Failed to load admin data. Ensure you have admin access.');
      }
    });
  }, []);

  const totalFailed = queues?.reduce((sum, q) => sum + q.failed, 0) ?? 0;
  const totalWaiting = queues?.reduce((sum, q) => sum + q.waiting, 0) ?? 0;

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-ll-error/10 border border-ll-error/30 rounded-lg p-4 text-sm text-ll-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Monthly Cost"
          value={costs ? `$${costs.totalCost.toFixed(2)}` : '--'}
          sub={costs?.month}
        />
        <KpiCard
          label="Input Tokens"
          value={costs ? costs.totalInputTokens.toLocaleString() : '--'}
        />
        <KpiCard
          label="Queue Depth"
          value={String(totalWaiting)}
          sub={totalWaiting > 100 ? 'Warning: high queue depth' : 'Healthy'}
        />
        <KpiCard
          label="Failed Jobs"
          value={String(totalFailed)}
          sub={totalFailed > 0 ? 'Requires attention' : 'No failures'}
        />
      </div>

      <div>
        <h2 className="text-lg font-semibold text-ll-text mb-4">Quick Links</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { to: '/admin/users', label: 'Manage Users' },
            { to: '/admin/teams', label: 'Manage Teams' },
            { to: '/admin/costs', label: 'Cost Details' },
            { to: '/admin/queues', label: 'Queue Health' },
            { to: '/admin/audit', label: 'Audit Log' },
          ].map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="bg-ll-surface border border-ll-border rounded-lg p-4 text-sm text-ll-muted hover:text-ll-cyan hover:border-ll-cyan/30 transition-colors"
            >
              {link.label} &rarr;
            </Link>
          ))}
        </div>
      </div>

      {queues && queues.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-ll-text mb-4">Queue Status</h2>
          <div className="space-y-3">
            {queues.map((q) => (
              <div key={q.name} className="bg-ll-surface border border-ll-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-ll-text">{q.name}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${q.failed > 0 ? 'bg-ll-error/20 text-ll-error' : 'bg-ll-success/20 text-ll-success'}`}
                  >
                    {q.failed > 0 ? 'Has failures' : 'Healthy'}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-2 text-xs text-ll-muted">
                  <div>
                    Waiting: <span className="text-ll-text">{q.waiting}</span>
                  </div>
                  <div>
                    Active: <span className="text-ll-text">{q.active}</span>
                  </div>
                  <div>
                    Completed: <span className="text-ll-text">{q.completed}</span>
                  </div>
                  <div>
                    Failed:{' '}
                    <span className={q.failed > 0 ? 'text-ll-error' : 'text-ll-text'}>
                      {q.failed}
                    </span>
                  </div>
                  <div>
                    Delayed: <span className="text-ll-text">{q.delayed}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
